// @ts-nocheck
// client/src/v2/nota-composer.tsx
// O COMPOSITOR DE NOTA — um só, em todo lugar.
//
// Ele nasceu fundido na tela do livro (~140 linhas coladas em 14 useState do
// BookStudyScreen), e por isso o caderno pessoal nascia mudo. Agora é este
// componente que abre no caderno do livro E no caderno pessoal. A diferença
// entre os dois é UMA prop: `livro`. Com livro existe âncora de página; sem
// livro, não. Nada mais muda.
//
// TODA NOTA NASCE COMO "NOTA" — 24/09/2026.
// Antes a pessoa escolhia entre Nota / Grifo / Ideia / Pergunta, e o tipo
// decidia a APARÊNCIA: grifo saía em serifada grande, ideia em sans pequena.
// Eram dois assuntos num controle só, e para conseguir a letra que queria a
// pessoa tinha de aceitar um rótulo que talvez não fosse o dela. Agora o
// rótulo sumiu da criação e a aparência virou escolha explícita: cor e fonte.
//
// Zero cor literal: 4 paletas, 2 delas claras.

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { aoVoltarFechar } from './platform';
import { api } from '../services/apiV2';
import { Icon } from './icons';
import { CameraCapture } from './camera-capture';
import { AudioPlayer, useVoiceRecorder, fmtTime, DEFAULT_WAVE } from './audio';

const CORES = ['none', 'ember', 'sage', 'plum'];
const COR_VAR: Record<string, string> = { ember: 'var(--ember)', sage: 'var(--sage)', plum: 'var(--plum)' };

/** As três letras que a casa tem. `sans` é o corpo do app, `serif` é a
 *  Instrument Serif dos títulos, `mono` é a Geist Mono dos rótulos. */
export const FONTES = [
  { key: 'sans',  label: 'Padrão',   familia: 'inherit' },
  { key: 'serif', label: 'Serifada', familia: 'Instrument Serif, Georgia, serif' },
  { key: 'mono',  label: 'Máquina',  familia: 'Geist Mono, monospace' },
];

/** Barra de marcação: ela ESCREVE o Markdown no texto. O que a pessoa vê é o
 *  que fica guardado, e o que ela copia para fora continua fazendo sentido. */
const FERRAMENTAS = [
  { rotulo: 'H', titulo: 'Título',    prefixo: '## ',    linha: true },
  { rotulo: 'B', titulo: 'Negrito',   envolve: '**',     volta: '**' },
  { rotulo: 'I', titulo: 'Itálico',   envolve: '_',      volta: '_' },
  { rotulo: '•', titulo: 'Lista',     prefixo: '- ',     linha: true },
  { rotulo: '☑', titulo: 'Checklist', prefixo: '- [ ] ', linha: true },
  { rotulo: '❝', titulo: 'Citação',   prefixo: '> ',     linha: true },
];

/** A PERGUNTA QUE ABRIU A NOTA (31/10/2026). O "Responder no caderno" do
 *  insight — na folha da Travessia e no caderno vazio — abre o editor com a
 *  pergunta do Mentor por cima do campo, para quem responde não a perder de
 *  vista. Ela não entra no campo (ninguém precisa apagar nada para escrever):
 *  ao guardar, vira o título da nota, ou a citação na 1ª linha se a pessoa
 *  já deu um título seu. `origem` é a linha miúda acima ("Insight · p. 38"). */
export type ContextoDaNota = { pergunta: string; origem?: string };

/** O que a nota guarda da pergunta: título se vazio; senão, citação no topo do texto. */
export function comPergunta(contexto: ContextoDaNota | null | undefined, titulo: string, texto: string): { title: string | null; text: string } {
  const t = titulo.trim();
  if (!contexto?.pergunta) return { title: t || null, text: texto };
  if (!t) return { title: contexto.pergunta, text: texto };
  return { title: t, text: `> ${contexto.pergunta}\n\n${texto}` };
}

export const NotaComposer: React.FC<{
  session: any;
  /** Onde a nota vai cair. Num caderno de livro, o id do caderno dele. */
  notebookId?: string | null;
  nomeDoCaderno?: string;
  /** Só quando a nota nasce dentro de um livro. É o que traz a âncora. */
  livro?: { id: string; titulo?: string; paginaAtual?: number; totalPaginas?: number; unidade?: string } | null;
  /** Rótulo da âncora conforme a unidade (p. / cap. / pos.). */
  pageLabel?: (p: number | null) => string;
  aberto: boolean;
  modoInicial?: 'texto' | 'voz' | 'foto';
  /** O que a pessoa já digitou na barra de captura antes de o editor abrir. */
  textoInicial?: string;
  /** A pergunta que a nota responde (ver ContextoDaNota). */
  contexto?: ContextoDaNota | null;
  onFechar: () => void;
  onCriada: (n: any) => void;
}> = ({ session, notebookId, nomeDoCaderno, livro, pageLabel, aberto, modoInicial = 'texto', textoInicial = '', contexto = null, onFechar, onCriada }) => {
  const [modo, setModo] = useState<'texto' | 'voz' | 'foto'>(modoInicial);
  const [cor, setCor] = useState('none');
  const [fonte, setFonte] = useState('sans');
  const [texto, setTexto] = useState('');
  const [titulo, setTitulo] = useState('');
  const [foto, setFoto] = useState<File | null>(null);
  const [fotoUrl, setFotoUrl] = useState<string | null>(null);
  const [temPagina, setTemPagina] = useState(true);
  const [pagina, setPagina] = useState<number>(livro?.paginaAtual || 0);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const areaRef = useRef<HTMLTextAreaElement | null>(null);
  const voz = useVoiceRecorder();

  const rotulo = pageLabel || ((p: number | null) => (p == null ? 'sem página' : `p. ${p}`));

  // O componente fica montado (fechado devolve null), então o `useState`
  // acima só leu o modo da PRIMEIRA abertura: o microfone e a câmera da barra
  // abriam o editor de texto. A cada abertura, vale o modo pedido — e o que já
  // foi digitado na barra entra no texto (pente fino, UX-07).
  useEffect(() => {
    if (!aberto) return;
    setModo(modoInicial);
    if (textoInicial) setTexto((t) => (t ? t : textoInicial));
  }, [aberto]); // eslint-disable-line react-hooks/exhaustive-deps

  const limpar = useCallback(() => {
    setModo('texto'); setTexto(''); setTitulo(''); setCor('none'); setFonte('sans');
    setFoto(null); setFotoUrl(null); setErro(null);
    setTemPagina(true); setPagina(livro?.paginaAtual || 0);
    voz.reset();
  }, [voz, livro]);

  // A página de quem abre vale a cada abertura: o "Responder no caderno" do
  // insight abre na página do insight, não na que estava quando a folha montou.
  useEffect(() => { if (aberto) setPagina(livro?.paginaAtual || 0); }, [aberto]); // eslint-disable-line

  const fechar = () => { limpar(); onFechar(); };

  const aplicar = (f: any) => {
    const el = areaRef.current;
    if (!el) return;
    const ini = el.selectionStart;
    const fim = el.selectionEnd;
    if (f.linha) {
      // Marcação de linha entra no COMEÇO da linha do cursor: "## " no miolo
      // de uma frase não vira título nenhum.
      const quebra = texto.lastIndexOf('\n', ini - 1) + 1;
      const novo = texto.slice(0, quebra) + f.prefixo + texto.slice(quebra);
      setTexto(novo);
      requestAnimationFrame(() => { el.focus(); el.setSelectionRange(ini + f.prefixo.length, fim + f.prefixo.length); });
      return;
    }
    const sel = texto.slice(ini, fim);
    setTexto(texto.slice(0, ini) + f.envolve + sel + f.volta + texto.slice(fim));
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(ini + f.envolve.length, ini + f.envolve.length + sel.length); });
  };

  const guardar = async () => {
    setSalvando(true); setErro(null);
    try {
      const corpo: any = {
        text: texto,
        title: titulo.trim() || null,
        color: cor,
        font: fonte,
        type: 'nota',
      };
      if (notebookId) corpo.notebookId = notebookId;
      if (livro) {
        corpo.bookId = livro.id;
        corpo.page = temPagina ? pagina : null;
      }

      if (modo === 'voz' && voz.blob) {
        const up = await api.uploadBookNoteAudio(session, voz.blob, voz.nomeDoArquivo('nota'));
        corpo.audioUrl = up.url;
        corpo.audioDur = Math.max(1, Math.round(voz.dur || 1));
        corpo.wave = voz.wave && voz.wave.length ? voz.wave : DEFAULT_WAVE;
        corpo.type = 'audio';
      }
      if (modo === 'foto' && foto) {
        const up = await api.uploadBookNotePhoto(session, foto);
        corpo.photoUrl = up.url;
        corpo.photoKind = 'page';
      }
      if (!String(corpo.text || '').trim() && !corpo.audioUrl && !corpo.photoUrl) {
        setErro('Escreva algo, grave ou tire uma foto.');
        setSalvando(false);
        return;
      }
      // Só o que a pessoa fez conta para "nota vazia"; a pergunta vem junto.
      Object.assign(corpo, comPergunta(contexto, titulo, texto));

      const n = await api.criarNotaLivre(session, corpo);
      onCriada(n);
      limpar();
      onFechar();
    } catch (e: any) {
      setErro(e?.message || 'Não deu para guardar.');
    } finally {
      setSalvando(false);
    }
  };

  // O voltar do Android fecha a nota (como o X), em vez de sair do caderno com
  // ela aberta por cima. Ref: o ouvinte registrado vê sempre o `fechar` atual.
  const fecharRef = useRef(fechar);
  fecharRef.current = fechar;
  useEffect(() => (aberto ? aoVoltarFechar(() => fecharRef.current()) : undefined), [aberto]);

  if (!aberto) return null;

  const familia = (FONTES.find((f) => f.key === fonte) || FONTES[0]).familia;

  return (
    <div onClick={fechar} style={{
      position: 'fixed', inset: 0, zIndex: 9998, background: 'rgba(0,0,0,.55)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: '100%', maxWidth: 520, background: 'var(--paper)',
        borderTop: '1px solid var(--line-strong)', borderRadius: '26px 26px 0 0',
        padding: '14px 20px 26px', maxHeight: '92vh', overflowY: 'auto',
      }}>
        <div style={{ width: 34, height: 3.5, borderRadius: 3, background: 'var(--line-strong)', margin: '0 auto 14px' }} />

        {(nomeDoCaderno || livro?.titulo) && (
          <div className="mono" style={{
            fontSize: 9.5, letterSpacing: '0.14em', textTransform: 'uppercase',
            color: 'var(--ink-4)', marginBottom: 12,
          }}>Guardando em {nomeDoCaderno || livro?.titulo}</div>
        )}

        {contexto?.pergunta && (
          <div data-composer-pergunta style={{
            margin: '0 0 14px', padding: '10px 14px', borderLeft: '3px solid var(--ember)',
            background: 'var(--paper-2)', borderRadius: '0 12px 12px 0',
          }}>
            <div className="mono" style={{ fontSize: 9.5, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ember)' }}>
              Para o caderno{contexto.origem ? ` · ${contexto.origem}` : ''}
            </div>
            <p className="serif" style={{ margin: '6px 0 0', fontSize: 18, lineHeight: 1.3, color: 'var(--ink)' }}>{contexto.pergunta}</p>
          </div>
        )}

        {/* Texto · Voz · Foto */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
          {[['texto', 'Texto'], ['voz', 'Voz'], ['foto', 'Foto']].map(([k, l]) => (
            <button key={k} onClick={() => setModo(k as any)} style={{
              flex: 1, padding: '9px 0', borderRadius: 11, cursor: 'pointer', fontFamily: 'inherit',
              fontSize: 13, fontWeight: modo === k ? 700 : 500,
              background: modo === k ? 'var(--ember-soft)' : 'var(--paper-2)',
              border: `1px solid ${modo === k ? 'var(--ember)' : 'var(--line)'}`,
              color: modo === k ? 'var(--ember-2)' : 'var(--ink-3)',
            }}>{l}</button>
          ))}
        </div>

        {modo === 'texto' && (
          <>
            <input
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              placeholder="Título (opcional)"
              style={{
                width: '100%', padding: '9px 0 10px', marginBottom: 10, border: 'none',
                borderBottom: '1px solid var(--line)', background: 'transparent', outline: 'none',
                color: 'var(--ink)', fontSize: 18, fontFamily: 'Instrument Serif, Georgia, serif',
              }}
            />
            <div style={{ display: 'flex', gap: 6, marginBottom: 8, overflowX: 'auto' }}>
              {FERRAMENTAS.map((f) => (
                <button key={f.rotulo} onClick={() => aplicar(f)} title={f.titulo} style={{
                  flexShrink: 0, minWidth: 32, height: 28, borderRadius: 8, cursor: 'pointer',
                  background: 'var(--paper-2)', border: '1px solid var(--line)',
                  color: 'var(--ink-2)', fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit',
                }}>{f.rotulo}</button>
              ))}
            </div>
            <textarea
              ref={areaRef}
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              autoFocus
              placeholder={'O que você quer guardar?\n\nUse # para etiquetar e [[ ]] para citar outra nota.'}
              style={{
                width: '100%', minHeight: 150, padding: '12px 14px', borderRadius: 14,
                background: 'var(--paper-2)', border: '1px solid var(--line)',
                color: 'var(--ink)', fontSize: 15, lineHeight: 1.6,
                // A prévia é a própria escrita: a pessoa já digita com a
                // letra que a nota vai ter.
                fontFamily: familia, resize: 'vertical',
              }}
            />
          </>
        )}

        {modo === 'voz' && (
          <div style={{ padding: '8px 0 4px', textAlign: 'center' }}>
            {/* OS NOMES SAO OS DO GRAVADOR, e isto ja custou caro.
                Este bloco checava 'rec' e 'done'; o hook produz 'recording' e
                'recorded' (audio.tsx:162). Nenhum dos dois casava: tocar no
                microfone COMECAVA a gravar de verdade — permissao pedida,
                indicador aceso — e a tela nao mostrava nada. Sem cronometro,
                sem botao de parar. A gravacao seguia invisivel ate os 10 min,
                e a unica saida descartava o audio em silencio.

                Os outros dois consumidores do mesmo hook (book-study.tsx e
                clubs/estudo-caderno.tsx) sempre usaram os nomes certos — era
                so aqui. */}
            {voz.stage === 'idle' && (
              <button onClick={voz.start} style={{
                width: 72, height: 72, borderRadius: '50%', cursor: 'pointer',
                background: 'var(--ember)', border: 'none',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              }}><Icon name="mic" size={26} color="var(--paper)" /></button>
            )}
            {voz.stage === 'recording' && (
              <>
                <div className="serif" style={{ fontSize: 30, color: 'var(--ember)' }}>{fmtTime(voz.sec)}</div>
                <button onClick={voz.stop} style={{
                  marginTop: 12, padding: '11px 22px', borderRadius: 999, cursor: 'pointer',
                  background: 'var(--ember)', border: 'none', color: 'var(--paper)',
                  fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
                }}>Parar</button>
              </>
            )}
            {voz.stage === 'recorded' && voz.url && (
              <>
                <AudioPlayer url={voz.url} dur={voz.dur} wave={voz.wave} />
                <button onClick={voz.regravar} style={ligacao}>Gravar de novo</button>
              </>
            )}
            {voz.err && <div style={{ color: 'var(--ember-2)', fontSize: 13, marginTop: 10 }}>{voz.err}</div>}
            <textarea
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              placeholder="Legenda (opcional)"
              style={legenda}
            />
          </div>
        )}

        {modo === 'foto' && (
          <div>
            {!fotoUrl ? (
              <CameraCapture open title="Foto da nota"
                onCapture={(f: File) => { setFoto(f); setFotoUrl(URL.createObjectURL(f)); }}
                onClose={() => setModo('texto')} />
            ) : (
              <>
                <img src={fotoUrl} alt="" style={{ width: '100%', borderRadius: 12, display: 'block' }} />
                <button onClick={() => { setFoto(null); setFotoUrl(null); }} style={ligacao}>Tirar outra</button>
              </>
            )}
            <textarea
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              placeholder="Legenda (opcional)"
              style={legenda}
            />
          </div>
        )}

        {/* ── âncora de página: só existe dentro de um livro ── */}
        {livro && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginTop: 14 }}>
            <span className="mono" style={rotuloPequeno}>Âncora</span>
            {temPagina ? (
              <>
                <button onClick={() => setPagina((p) => Math.max(0, p - 1))} style={passo}>−</button>
                <span className="mono" style={{ fontSize: 13, color: 'var(--ink)', minWidth: 58, textAlign: 'center' }}>
                  {rotulo(pagina)}
                </span>
                <button onClick={() => setPagina((p) => Math.min(livro.totalPaginas || 1000000, p + 1))} style={passo}>+</button>
              </>
            ) : (
              <span style={{ fontSize: 13, color: 'var(--ink-4)' }}>sem página</span>
            )}
            <button onClick={() => setTemPagina((v) => !v)} style={{ ...ligacao, marginLeft: 'auto', marginTop: 0 }}>
              {temPagina ? 'Sem página' : 'Usar página'}
            </button>
          </div>
        )}

        {/* ── cor e fonte ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginTop: 14 }}>
          <span className="mono" style={rotuloPequeno}>Cor</span>
          {CORES.map((c) => (
            <button key={c} onClick={() => setCor(c)} aria-label={c} style={{
              width: 20, height: 20, borderRadius: '50%', cursor: 'pointer', padding: 0,
              background: c === 'none' ? 'transparent' : COR_VAR[c],
              border: `1px solid ${cor === c ? 'var(--ink-2)' : 'var(--line-strong)'}`,
              boxShadow: cor === c ? '0 0 0 2px var(--paper), 0 0 0 3px var(--ink-3)' : 'none',
            }} />
          ))}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 11 }}>
          <span className="mono" style={rotuloPequeno}>Letra</span>
          {FONTES.map((f) => (
            <button key={f.key} onClick={() => setFonte(f.key)} style={{
              padding: '6px 12px', borderRadius: 999, cursor: 'pointer',
              fontFamily: f.familia === 'inherit' ? 'inherit' : f.familia,
              fontSize: 12.5, fontWeight: fonte === f.key ? 700 : 500,
              background: fonte === f.key ? 'var(--ember-soft)' : 'transparent',
              border: `1px solid ${fonte === f.key ? 'var(--ember)' : 'var(--line)'}`,
              color: fonte === f.key ? 'var(--ember-2)' : 'var(--ink-3)',
            }}>{f.label}</button>
          ))}
        </div>

        {erro && <div style={{ color: 'var(--ember-2)', fontSize: 13, marginTop: 10 }}>{erro}</div>}

        <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
          <button onClick={fechar} style={{
            padding: '13px 20px', borderRadius: 14, cursor: 'pointer', fontFamily: 'inherit',
            background: 'transparent', border: '1px solid var(--line)', color: 'var(--ink-3)', fontSize: 14,
          }}>Cancelar</button>
          <button onClick={guardar} disabled={salvando} style={{
            flex: 1, padding: '13px 0', borderRadius: 14, border: 'none',
            cursor: salvando ? 'default' : 'pointer', fontFamily: 'inherit',
            background: salvando ? 'var(--paper-3)' : 'var(--ember)',
            color: salvando ? 'var(--ink-4)' : 'var(--paper)',
            fontSize: 14.5, fontWeight: 600,
          }}>{salvando ? 'Guardando…' : 'Guardar'}</button>
        </div>
      </div>
    </div>
  );
};

/** O campo da barra de captura (pente fino, UX-07).
 *
 *  Parecia campo e era botão: quem tocava e começava a digitar não via nada
 *  acontecer, e a ideia se perdia. Agora é um campo de verdade: tocar (ou
 *  focar pelo teclado) abre o editor já com o foco no texto, e o que chegar a
 *  ser digitado aqui vai junto para o editor. */
export const CampoDeCaptura: React.FC<{
  onAbrir: (texto: string) => void;
  style?: React.CSSProperties;
}> = ({ onAbrir, style }) => (
  <input
    value=""
    aria-label="Anote o que você leu"
    placeholder="Anote o que você leu…"
    onFocus={() => onAbrir('')}
    onChange={(e) => { if (e.target.value) onAbrir(e.target.value); }}
    style={{ minWidth: 0, fontFamily: 'inherit', outline: 'none', ...style }}
  />
);

/** A barra que abre o compositor. Fica no rodapé da lista de notas. */
export const BarraDeCaptura: React.FC<{ onAbrir: (modo: 'texto' | 'voz' | 'foto', texto?: string) => void }> = ({ onAbrir }) => (
  <div style={{
    position: 'sticky', bottom: 0, zIndex: 5,
    display: 'flex', alignItems: 'center', gap: 8, padding: '10px 0 14px',
    background: 'linear-gradient(180deg, transparent, var(--paper) 38%)',
  }}>
    <CampoDeCaptura onAbrir={(t) => onAbrir('texto', t)} style={{
      flex: 1, textAlign: 'left', padding: '13px 16px', borderRadius: 999,
      background: 'var(--paper-2)', border: '1px solid var(--line)',
      color: 'var(--ink)', fontSize: 14,
    }} />
    <button onClick={() => onAbrir('voz')} aria-label="Gravar" style={redondo}>
      <Icon name="mic" size={17} color="var(--ink-2)" />
    </button>
    <button onClick={() => onAbrir('foto')} aria-label="Foto" style={redondo}>
      <Icon name="camera" size={17} color="var(--ink-2)" />
    </button>
  </div>
);

const redondo: React.CSSProperties = {
  width: 44, height: 44, borderRadius: '50%', flexShrink: 0, cursor: 'pointer',
  background: 'var(--paper-2)', border: '1px solid var(--line)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const rotuloPequeno: React.CSSProperties = {
  fontSize: 9.5, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--ink-4)',
};
const passo: React.CSSProperties = {
  width: 30, height: 30, borderRadius: 9, cursor: 'pointer', fontFamily: 'inherit',
  background: 'var(--paper-2)', border: '1px solid var(--line)', color: 'var(--ink-2)', fontSize: 15,
};
const ligacao: React.CSSProperties = {
  marginTop: 10, background: 'transparent', border: 'none', cursor: 'pointer',
  color: 'var(--ink-3)', fontSize: 13, fontFamily: 'inherit',
};
const legenda: React.CSSProperties = {
  width: '100%', minHeight: 60, marginTop: 12, padding: '10px 12px', borderRadius: 12,
  background: 'var(--paper-2)', border: '1px solid var(--line)',
  color: 'var(--ink)', fontSize: 14, fontFamily: 'inherit', resize: 'vertical',
};

export default NotaComposer;
